import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useInvestigation } from '@/queries/investigations';
import { triageDepthLabel } from './triageLabels';
import type { Investigation, InvestigationStep } from '@/types/investigation';

type Usage = NonNullable<Investigation['debug']>['usage'];

function formatCost(usage: Usage) {
  if (!usage || (usage.inputTokens === 0 && usage.outputTokens === 0)) {
    return { text: 'n/a', title: 'usage unavailable for this run' };
  }
  // estimateCost returns null when the model id has no price on file.
  if (usage.estimatedCost === null || usage.estimatedCost === undefined) {
    return { text: 'n/a', title: 'no price on file for this model' };
  }
  return { text: `$${usage.estimatedCost.toFixed(4)}`, title: undefined };
}

function formatUsd(n: number) {
  return `$${n.toFixed(4)}`;
}

export function DebugTab({ ticketId, invId }: { ticketId: string; invId: string }) {
  // DebugTab is only ever reached for terminal runs (completed, or failed/
  // cancelled via the StoppedCard disclosure) — the record can't change
  // underneath it, so it opts out of the hook's default 3s poll.
  const { data } = useInvestigation(ticketId, invId, true, false);

  if (!data) {
    return <p className="p-2 text-xs text-muted-foreground">Loading debug data…</p>;
  }

  const debug = data.debug;
  const triage = data.triage;
  const cost = formatCost(debug?.usage);
  const steps = debug?.steps ?? [];
  const usedCounts = new Map<string, number>();
  steps.forEach(s => usedCounts.set(s.tool, (usedCounts.get(s.tool) ?? 0) + 1));

  // debug.usage.estimatedCost is the whole run; triage.usage.estimatedCost is only its share, so
  // the investigation share is the remainder rather than a second sum. Gated on the same
  // usage-availability check as `cost` — an n/a total should never show a split beside it.
  const triageCost = triage?.usage?.estimatedCost ?? null;
  const totalCost = debug?.usage?.estimatedCost ?? null;
  const costSplit =
    cost.text !== 'n/a' && totalCost != null && triageCost != null
      ? `${formatUsd(triageCost)} triage + ${formatUsd(totalCost - triageCost)} investigation`
      : null;

  function copyPromptAndContext() {
    const text = [debug?.systemPrompt, debug?.contextSummary].filter(Boolean).join('\n\n---\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  }

  return (
    <div className="space-y-3 p-2 text-sm">
      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        {data.model ? (
          <>
            <span>{data.model.id}</span>
            <span>{data.effort}</span>
          </>
        ) : (
          <span className="italic">model not yet resolved</span>
        )}
        <span>
          {debug?.usage
            ? `${debug.usage.inputTokens} in${debug.usage.cacheReadTokens ? ` (${debug.usage.cacheReadTokens} from cache)` : ''} / ${debug.usage.outputTokens} out tokens`
            : '— tokens'}
        </span>
        <span title={cost.title}>{cost.text}</span>
        {costSplit && <span>({costSplit})</span>}
      </div>

      {triage && (
        <details className="rounded border border-border p-2">
          <summary className="cursor-pointer text-xs font-medium">Triage</summary>
          <div className="mt-2 space-y-2 text-xs">
            <p>
              <span className="text-muted-foreground">Depth:</span> {triageDepthLabel(triage.depth)}
              {triage.skipped && (
                <Badge variant="secondary" className="ml-2">
                  skipped
                </Badge>
              )}
            </p>
            <p className="text-muted-foreground">{triage.reasoning}</p>
            {triage.plan.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-4">
                {triage.plan.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
            <p className="text-muted-foreground">
              {triage.durationMs}ms
              {triage.usage && ` — ${triage.usage.inputTokens} in / ${triage.usage.outputTokens} out — ${formatCost(triage.usage).text}`}
            </p>
          </div>
        </details>
      )}

      <details className="rounded border border-border p-2" open>
        <summary className="cursor-pointer text-xs font-medium">Prompt & context</summary>
        <div className="mt-2 flex justify-end">
          <Button type="button" size="xs" variant="ghost" onClick={copyPromptAndContext}>
            <Copy className="h-3 w-3" />
            Copy
          </Button>
        </div>
        <pre className="whitespace-pre-wrap text-xs">{debug?.systemPrompt ?? '—'}</pre>
        <pre className="whitespace-pre-wrap text-xs">{debug?.contextSummary ?? '—'}</pre>
      </details>

      <details className="rounded border border-border p-2">
        <summary className="cursor-pointer text-xs font-medium">Tools</summary>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {(debug?.toolCatalog ?? []).map(tool => (
            <Badge key={tool} variant="secondary">
              {tool} ({usedCounts.get(tool) ?? 0})
            </Badge>
          ))}
        </div>
        {steps.length > 0 && (
          <table className="mt-2 w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pr-2">Tool</th>
                <th className="pr-2">Args</th>
                <th className="pr-2">Result</th>
                <th className="pr-2">Duration</th>
              </tr>
            </thead>
            <tbody>
              {steps.map((step, i) => (
                <StepRow key={i} step={step} />
              ))}
            </tbody>
          </table>
        )}
      </details>

      <details className="rounded border border-border p-2">
        <summary className="cursor-pointer text-xs font-medium">Reasoning</summary>
        <pre className="mt-2 whitespace-pre-wrap text-xs">{debug?.reasoning ?? '—'}</pre>
      </details>

      <details className="rounded border border-border p-2">
        <summary className="cursor-pointer text-xs font-medium">Raw output</summary>
        <pre className="mt-2 whitespace-pre-wrap text-xs">{debug?.rawOutput ?? '—'}</pre>
      </details>
    </div>
  );
}

function StepRow({ step }: { step: InvestigationStep }) {
  return (
    <tr className={step.isError ? 'text-destructive' : undefined}>
      <td className="pr-2 align-top">{step.tool}</td>
      <td className="max-w-[16rem] truncate pr-2 align-top">{JSON.stringify(step.args)}</td>
      <td className="pr-2 align-top">
        <details>
          <summary className="cursor-pointer">preview</summary>
          <pre className="whitespace-pre-wrap">{step.resultPreview}</pre>
        </details>
      </td>
      <td className="pr-2 align-top">{step.durationMs}ms</td>
    </tr>
  );
}
